/**
 * Générateur de SKU automatique pour InventWee
 * Respecte la logique de l'application mobile
 */

interface SKUOptions {
  categorie: string;
  sousCategorie?: string;
  nom: string;
  zoneId?: string;
  boxId?: string;
}

/**
 * Génère un SKU automatique basé sur les informations de l'objet
 * Format: [CATEGORIE]-[SOUS_CATEGORIE]-[NOM]-[LOCALISATION]-[NUMERO]
 */
export function generateSKU(options: SKUOptions): string {
  const { categorie, sousCategorie, nom, zoneId, boxId } = options;
  
  // 1. Normaliser la catégorie (premiers caractères, majuscules)
  const catCode = normalizeCategory(categorie);
  
  // 2. Normaliser la sous-catégorie si présente
  const sousCatCode = sousCategorie ? normalizeSubCategory(sousCategorie) : '';
  
  // 3. Normaliser le nom (premiers caractères significatifs)
  const nomCode = normalizeName(nom);
  
  // 4. Déterminer la localisation
  const localisation = getLocalisationCode(zoneId, boxId);
  
  // 5. Générer un numéro séquentiel (simulé pour l'instant)
  const numero = generateSequentialNumber();
  
  // 6. Assembler le SKU
  const skuParts = [catCode, sousCatCode, nomCode, localisation, numero].filter(Boolean);
  
  return skuParts.join('-').toUpperCase();
}

/**
 * Normalise une catégorie en code court
 */
function normalizeCategory(categorie: string): string {
  const categoryMap: { [key: string]: string } = {
    'Activités nature': 'ACTNAT',
    'Bible & pédagogie spirituelle': 'BIBLE',
    'Bureautique': 'BUREAU',
    'Divers & vie quotidienne': 'DIVERS',
    'Jeux créé / personnalisés': 'JEUXCRE',
    'Jeux de société': 'JEUXSOC',
    'Jeux extérieurs': 'JEUXEXT',
    'Jouets et jeux d\'enfants': 'JOUETS',
    'Matériel créatif & manuel': 'CREATIF',
    'Matériel électronique': 'ELECTRO',
    'Matériel vidéo': 'VIDEO',
    'Musique, son & lumières': 'MUSIQUE',
    'Pédagogie': 'PEDAGO',
    'Sécurité & encadrement': 'SECURITE'
  };
  
  return categoryMap[categorie] || abbreviateText(categorie, 6);
}

/**
 * Normalise une sous-catégorie en code court
 */
function normalizeSubCategory(sousCategorie: string): string {
  const subCategoryMap: { [key: string]: string } = {
    'Camping & itinérance': 'CAMP',
    'Plage / Lac': 'PLAGE',
    'Randonnée': 'RANDO',
    'Bibles': 'BIBLE',
    'Fourniture de bureau': 'FOURN',
    'Ateliers cuisine': 'CUISINE',
    'Organisation': 'ORG',
    'Jeux collectifs': 'COLLECT',
    'Jeux immersifs': 'IMMERS',
    'Classiques': 'CLASS',
    'Coopératifs / éducatifs': 'COOP',
    'Baby-foot / ping-pong': 'SPORT',
    'Ballons & sports': 'BALLON',
    'Jouets': 'JOUET',
    'Arts plastiques': 'ARTS',
    'Décors & costumes': 'DECOR',
    'Travaux manuels': 'MANUEL',
    'Arduino': 'ARDUINO',
    'Divers': 'DIV',
    'Piles / batteries': 'PILES',
    'Matériel visuel': 'VISUEL',
    'Autres': 'AUTRE',
    'Instruments': 'INSTRU',
    'Matériel lumière': 'LUMIERE',
    'Matériel Sono': 'SONO',
    'Supports pédagogiques': 'SUPPORT',
    'Encadrement activités': 'ENCADR',
    'Infirmerie': 'INFIRM'
  };
  
  return subCategoryMap[sousCategorie] || abbreviateText(sousCategorie, 6);
}

/**
 * Normalise un nom d'objet en code court
 */
function normalizeName(nom: string): string {
  // Supprimer les caractères spéciaux et espaces
  const cleaned = nom
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  
  // Prendre les premiers mots significatifs
  const words = cleaned.split(' ').filter(word => word.length > 2);
  
  if (words.length === 0) return 'OBJ';
  if (words.length === 1) return abbreviateText(words[0], 4);
  
  // Prendre les 2 premiers mots et les abréger
  const firstTwo = words.slice(0, 2);
  return firstTwo.map(word => abbreviateText(word, 3)).join('');
}

/**
 * Détermine le code de localisation
 */
function getLocalisationCode(zoneId?: string, boxId?: string): string {
  if (boxId) {
    return `BOX${boxId.slice(-2)}`; // Derniers 2 caractères de l'ID de box
  }
  if (zoneId) {
    // Extraire la lettre de l'armoire (ex: ROB2-0-ARM-A -> A)
    const match = zoneId.match(/ARM-([A-Z])/);
    if (match) {
      return `ARM${match[1]}`;
    }
    return `ZONE${zoneId.slice(-2)}`;
  }
  return 'UNK'; // Unknown location
}

/**
 * Génère un numéro séquentiel (simulé)
 * En production, ce serait basé sur un compteur en base de données
 */
function generateSequentialNumber(): string {
  // Simulation d'un numéro séquentiel
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
  return `${timestamp}${random}`;
}

/**
 * Abrège un texte en gardant les caractères significatifs
 */
function abbreviateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text.toUpperCase();
  
  // Prendre les premières lettres significatives
  const words = text.split(' ');
  if (words.length === 1) {
    return text.substring(0, maxLength).toUpperCase();
  }
  
  // Prendre les premières lettres de chaque mot
  const initials = words.map(word => word.charAt(0)).join('');
  if (initials.length <= maxLength) {
    return initials.toUpperCase();
  }
  
  return initials.substring(0, maxLength).toUpperCase();
}

/**
 * Valide un SKU généré
 */
export function validateSKU(sku: string): boolean {
  // Un SKU valide doit contenir au moins 3 parties séparées par des tirets
  const parts = sku.split('-');
  return parts.length >= 3 && parts.every(part => part.length > 0);
}

/**
 * Extrait les informations d'un SKU
 */
export function parseSKU(sku: string): {
  categorie?: string;
  sousCategorie?: string;
  nom?: string;
  localisation?: string;
  numero?: string;
} {
  const parts = sku.split('-');
  if (parts.length < 3) return {};
  
  return {
    categorie: parts[0],
    sousCategorie: parts[1] || undefined,
    nom: parts[2] || undefined,
    localisation: parts[3] || undefined,
    numero: parts[4] || undefined
  };
}

/**
 * Exemples de SKU générés
 */
export const SKU_EXAMPLES = {
  'Jeux extérieurs': {
    'Ballons & sports': 'JEUXEXT-BALLON-FOOTBALL-ARMA-123456',
    'Baby-foot / ping-pong': 'JEUXEXT-SPORT-BABYFOOT-ARMB-789012'
  },
  'Matériel électronique': {
    'Arduino': 'ELECTRO-ARDUINO-KIT-ARMC-345678',
    'Piles / batteries': 'ELECTRO-PILES-BATTERIE-ARMD-901234'
  },
  'Jeux de société': {
    'Classiques': 'JEUXSOC-CLASS-MONOPOLY-ARME-567890',
    'Coopératifs / éducatifs': 'JEUXSOC-COOP-PANDEMIC-ARMF-123456'
  }
};